// src/components/Arena.tsx
import { AD_SLOTS } from "../config/adSlots";
import React, { useRef, useState, useEffect } from "react";
import DropZone from "./DropZone";
import { useElementStore } from "../store/elementStore";
import VFXCanvas from "./VFXCanvas";
import Celebration from "./Celebration";
import { playSound } from "../services/soundService";
import ElementCanvas from "./ElementCanvas";
import FunFact from "./FunFact";
import DiscoveryModal from "./ui/DiscoveryModal";
import AchievementToast from "./ui/AchievementToast";
import InteractiveWalkthrough from "./tutorial/InteractiveWalkthrough";
import HintSystem from "./ui/HintSystem";
import { checkAchievements, addXP, Achievement } from "../services/progressionService";
import { shouldShowWalkthrough } from "../services/tutorialService";
import { triggerHaptic } from "../services/capacitorService";
import AdBanner from "./ads/AdBanner";

interface ArenaProps {
  customSelectHandler?: (id: string, zoneIndex: number) => void;
  customCombineHandler?: () => void;
  customUnlock?: (id: string) => void;
  restrictedElements?: string[];
}

const Arena: React.FC<ArenaProps> = ({
  customSelectHandler,
  customCombineHandler,
  customUnlock,
  restrictedElements,
}) => {
  const vfxRef = useRef<any>(null);
  const [showResult, setShowResult] = useState(false);
  const [resultId, setResultId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [celebrate, setCelebrate] = useState(false);
  const [lastDiscovered, setLastDiscovered] = useState<string | null>(null);

  // New: Discovery modal and achievements
  const [showDiscoveryModal, setShowDiscoveryModal] = useState(false);
  const [discoveredElement, setDiscoveredElement] = useState<any>(null);
  const [isFirstDiscovery, setIsFirstDiscovery] = useState(false);
  const [achievementToShow, setAchievementToShow] = useState<Achievement | null>(null);

  // Tutorial walkthrough
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);

  // Get store values first
  const all = useElementStore((s) => s.all);
  const selected = useElementStore((s) => s.selected);
  const discoverRecipe = useElementStore((s) => s.discoverRecipe);
  const select = useElementStore((s) => s.select);
  const unlockedSet = useElementStore((s) => s.unlocked);
  const defaultUnlock = useElementStore((s) => s.unlock);
  const unlockElement = customUnlock || defaultUnlock;
  const clear = useElementStore((s) => s.clearSelection);

  const canCombine = selected.filter(Boolean).length >= 2;

  // Check if we should show the walkthrough on mount
  useEffect(() => {
    if (shouldShowWalkthrough() && !customCombineHandler) {
      // Only show in Free Forge mode (when no custom handlers)
      setShowWalkthrough(true);
    }
  }, [customCombineHandler]);

  // Auto-advance tutorial based on game state
  useEffect(() => {
    if (!showWalkthrough) return;

    // Step 1: "Select Fire" - advances when Fire is in a dropzone slot
    if (tutorialStep === 1) {
      const fireInSlot = selected[0] === 'fire' || selected[1] === 'fire';
      if (fireInSlot) {
        setTutorialStep(2);
      }
    }

    // Step 2: "Select Water" - advances when both slots are filled
    if (tutorialStep === 2) {
      const bothFilled = selected[0] && selected[1];
      if (bothFilled) {
        setTutorialStep(3);
      }
    }

    // Step 3: "Combine!" - advances when steam is discovered
    if (tutorialStep === 3 && lastDiscovered === 'steam') {
      setTutorialStep(4);
    }
  }, [showWalkthrough, tutorialStep, selected, lastDiscovered]);

  const [funFactData, setFunFactData] = useState<{
    ingredients: string[];
    result: string;
  } | null>(null);

  const handleSelect = (id: string, zoneIndex: number) => {
    if (restrictedElements && !restrictedElements.includes(id)) {
      triggerHaptic('error');
      playSound("error");
      setErrorMessage("That element isn't available here.");
      setTimeout(() => setErrorMessage(null), 2000);
      return;
    }
    triggerHaptic('light');
    if (customSelectHandler) {
      customSelectHandler(id, zoneIndex);
    } else {
      select(id, zoneIndex);
      playSound("select");
    }
  };

  const handleCombine = () => {
    if (!canCombine) return;

    triggerHaptic('medium');
    playSound("combine");

    if (customCombineHandler) {
      customCombineHandler();
      return;
    }

    const ingredients = selected.filter(Boolean);
    const result = discoverRecipe(ingredients);

    if (result) {
      if (unlockedSet.has(result)) {
        triggerHaptic('error');
        playSound("error");
        setErrorMessage("You already discovered that element!");
        setTimeout(() => setErrorMessage(null), 2000);
      } else {
        triggerHaptic('success');
        playSound("discover");
        setFunFactData({ ingredients, result });
        setResultId(result);
        setShowResult(true);
        setCelebrate(true);
        setLastDiscovered(result);
        if (vfxRef.current?.playCombineEffect) {
          vfxRef.current.playCombineEffect(result);
        }
        unlockElement(result);

        // NEW: Show epic discovery modal
        const element = all[result];
        setDiscoveredElement(element);
        setIsFirstDiscovery(unlockedSet.size === 0);
        setShowDiscoveryModal(true);

        // NEW: Award XP and check for achievements
        const tierXP = element.tier * 10; // Higher tier = more XP
        addXP(tierXP);

        // Check achievements
        const newAchievements = checkAchievements(unlockedSet, 0);
        if (newAchievements.length > 0) {
          // Show first achievement in toast
          setTimeout(() => {
            setAchievementToShow(newAchievements[0]);
          }, 2000); // Show after discovery modal
        }
      }
    } else {
      triggerHaptic('error');
      playSound("error");
      setErrorMessage("Invalid combination! Try something else.");
      setTimeout(() => setErrorMessage(null), 2000);
    }
  };

  const handleEffectComplete = () => {
    clear();
    setTimeout(() => {
      setShowResult(false);
      setResultId(null);
    }, 1500);
  };

  const handleClearAll = () => {
    triggerHaptic('light');
    clear();
    setErrorMessage(null);
    playSound("error");
  };

  return (
    <main
      className="flex-1 relative flex flex-col items-center justify-center p-4"
      style={{
        background: `linear-gradient(135deg, ${require('../theme/designTokens').colors.background.primary} 0%, #1a1f3a 50%, #2d1b4e 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Interactive Walkthrough for first-time users */}
      {showWalkthrough && (
        <InteractiveWalkthrough
          currentStep={tutorialStep}
          onComplete={() => setShowWalkthrough(false)}
          onSkip={() => setShowWalkthrough(false)}
          onStepChange={(step) => setTutorialStep(step)}
        />
      )}

      {/* --- LAST DISCOVERED BADGE --- */}
      {lastDiscovered && (
        <div className="absolute top-4 left-4 flex items-center bg-gray-900 bg-opacity-75 p-2 rounded-lg space-x-2 z-20">
          <ElementCanvas e={all[lastDiscovered]} size={32} />
          <span className="text-white text-sm">{all[lastDiscovered].name}</span>
        </div>
      )}

      <VFXCanvas ref={vfxRef} onEffectComplete={handleEffectComplete} />
      <Celebration trigger={celebrate} onComplete={() => setCelebrate(false)} />

      {/* NEW: Epic Discovery Modal */}
      <DiscoveryModal
        element={discoveredElement}
        isOpen={showDiscoveryModal}
        onClose={() => setShowDiscoveryModal(false)}
        isFirstDiscovery={isFirstDiscovery}
      />

      {/* NEW: Achievement Toast */}
      <AchievementToast
        achievement={achievementToShow}
        onClose={() => setAchievementToShow(null)}
      />

      <div className="z-10 w-full max-w-md mx-auto">
        {showResult && resultId && (
          <div className="mb-6 flex flex-col items-center">
            <ElementCanvas e={all[resultId]} size={64} />
          </div>
        )}

        {errorMessage && (
          <div
            style={{
              marginBottom: '1.5rem',
              background: 'linear-gradient(135deg, rgba(220, 38, 38, 0.9) 0%, rgba(185, 28, 28, 0.9) 100%)',
              color: 'white',
              padding: '1rem 1.5rem',
              borderRadius: '1rem',
              textAlign: 'center',
              border: '2px solid rgba(239, 68, 68, 0.6)',
              boxShadow: '0 8px 20px rgba(220, 38, 38, 0.4)',
              animation: 'shake 0.5s ease-in-out',
              fontSize: '1rem',
              fontWeight: 600,
            }}
          >
            ⚠️ {errorMessage}
          </div>
        )}

        <div className="flex justify-center space-x-4 sm:space-x-6 mb-6">
          <DropZone zoneIndex={0} customSelectHandler={handleSelect} />
          <DropZone zoneIndex={1} customSelectHandler={handleSelect} />
        </div>

        {!customCombineHandler && (
          <div className="flex flex-col sm:flex-row gap-3 justify-center w-full mb-9">
            <button
              disabled={!canCombine}
              onClick={handleCombine}
              style={{
                background: canCombine
                  ? require('../theme/designTokens').colors.gradients.primary
                  : '#4B5563',
                border: 'none',
                color: 'white',
                padding: '0.75rem 2rem',
                borderRadius: '1rem',
                fontSize: '1.1rem',
                fontWeight: 700,
                cursor: canCombine ? 'pointer' : 'not-allowed',
                opacity: canCombine ? 1 : 0.5,
                boxShadow: canCombine
                  ? `0 8px 20px ${require('../theme/designTokens').colors.brand.primary}40`
                  : 'none',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
              }}
              onMouseEnter={(e) => {
                if (canCombine) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = `0 12px 24px ${require('../theme/designTokens').colors.brand.primary}60`;
                }
              }}
              onMouseLeave={(e) => {
                if (canCombine) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = `0 8px 20px ${require('../theme/designTokens').colors.brand.primary}40`;
                }
              }}
              className="w-full sm:w-auto"
            >
              <span style={{ position: 'relative', zIndex: 1 }}>
                ✨ Combine
              </span>
            </button>

            <button
              onClick={handleClearAll}
              disabled={!selected.some(Boolean)}
              style={{
                background: selected.some(Boolean)
                  ? 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)'
                  : '#4B5563',
                border: 'none',
                color: selected.some(Boolean) ? 'white' : '#9CA3AF',
                padding: '0.75rem 2rem',
                borderRadius: '1rem',
                fontSize: '1.1rem',
                fontWeight: 700,
                cursor: selected.some(Boolean) ? 'pointer' : 'not-allowed',
                opacity: selected.some(Boolean) ? 1 : 0.5,
                boxShadow: selected.some(Boolean)
                  ? '0 8px 20px rgba(239, 68, 68, 0.4)'
                  : 'none',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                if (selected.some(Boolean)) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(239, 68, 68, 0.6)';
                }
              }}
              onMouseLeave={(e) => {
                if (selected.some(Boolean)) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(239, 68, 68, 0.4)';
                }
              }}
              className="w-full sm:w-auto"
            >
              🗑️ Clear All
            </button>
          </div>
        )}

        {funFactData && (
          <FunFact
            ingredients={funFactData.ingredients}
            result={funFactData.result}
          />
        )}
      </div>

      {/* Hint System - floating button for hints */}
      {!customCombineHandler && (
        <HintSystem unlockedElements={Array.from(unlockedSet)} />
      )}

      {/* Gameplay Banner Ad - Fixed at bottom */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: 'rgba(17, 24, 39, 0.95)',
          borderTop: '1px solid rgba(75, 85, 99, 0.3)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <AdBanner
          adSlot={AD_SLOTS.gameplay}
          adFormat="horizontal"
          fullWidthResponsive={true}
          style={{ maxHeight: '90px', margin: '0 auto' }}
        />
      </div>
    </main>
  );
};

export default Arena;

// // src/components/Arena.tsx
// import React, { useRef, useState } from 'react';
// import DropZone from './DropZone';
// import { useElementStore } from '../store/elementStore';
// import VFXCanvas from './VFXCanvas';

// // ← import Celebration and playSound
// import Celebration from './Celebration';
// import { playSound } from '../services/soundService';

// interface ArenaProps {
//   customSelectHandler?: (id: string, zoneIndex: number) => void;
//   customCombineHandler?: () => void;
//   customUnlock?: (id: string) => void;
//   restrictedElements?: string[];
// }

// const Arena: React.FC<ArenaProps> = ({
//   customSelectHandler,
//   customCombineHandler,
//   customUnlock,
//   restrictedElements
// }) => {
//   const vfxRef = useRef<any>(null);
//   const [showResult, setShowResult] = useState(false);
//   const [resultId, setResultId] = useState<string | null>(null);
//   const [showError, setShowError] = useState(false);
//   // ← celebration trigger
//   const [celebrate, setCelebrate] = useState(false);

//   const all = useElementStore(s => s.all);
//   const selected = useElementStore(s => s.selected);
//   const discoverRecipe = useElementStore(s => s.discoverRecipe);
//   const select = useElementStore(s => s.select);
//   const unlock = useElementStore(s => customUnlock || s.unlock);
//   const clear = useElementStore(s => s.clearSelection);

//   const canCombine = selected.filter(Boolean).length >= 2;

//   const handleSelect = (id: string, zoneIndex: number) => {
//     if (restrictedElements && !restrictedElements.includes(id)) {
//       // play an “error” sound when tapping disallowed
//       playSound('error');
//       return;
//     }
//     if (customSelectHandler) {
//       customSelectHandler(id, zoneIndex);
//     } else {
//       select(id, zoneIndex);
//       // play a “select” click
//       playSound('select');
//     }
//   };

//   const handleCombine = () => {
//     if (!canCombine) return;

//     // play the combine click
//     playSound('combine');

//     if (customCombineHandler) {
//       customCombineHandler();
//       return;
//     }

//     const ingredients = selected.filter(id => !!id);
//     const result = discoverRecipe(ingredients);

//     if (result) {
//       // success!
//       playSound('discover');
//       setResultId(result);
//       setShowResult(true);
//       setCelebrate(true);

//       if (vfxRef.current?.playCombineEffect) {
//         vfxRef.current.playCombineEffect(result);
//       }
//     } else {
//       // error combo
//       playSound('error');
//       setShowError(true);
//       setTimeout(() => setShowError(false), 2000);
//     }
//   };

//   const handleEffectComplete = () => {
//     clear();
//     setTimeout(() => {
//       setShowResult(false);
//       setResultId(null);
//     }, 1500);
//   };

//   const handleClearAll = () => {
//     clear();
//     setShowError(false);
//     playSound('error'); // or another sound if desired
//   };

//   return (
//     <main className="flex-1 relative flex flex-col items-center justify-center bg-gray-800 p-4">
//       {/* VFX Layer */}
//       <VFXCanvas ref={vfxRef} onEffectComplete={handleEffectComplete} />

//       {/* Celebration overlay */}
//       <Celebration
//         trigger={celebrate}
//         onComplete={() => setCelebrate(false)}
//       />

//       <div className="z-10 w-full max-w-md mx-auto">
//         {showResult && resultId && (
//           <div className="mb-6 flex flex-col items-center">
//             {/* you can render the resulting element here if desired */}
//           </div>
//         )}

//         {showError && (
//           <div className="mb-6 bg-red-900 bg-opacity-80 text-red-100 px-4 py-2 rounded-lg text-center">
//             Invalid combination! Try something else.
//           </div>
//         )}

//         <div className="flex justify-center space-x-4 sm:space-x-6 mb-6">
//           <DropZone zoneIndex={0} customSelectHandler={handleSelect} />
//           <DropZone zoneIndex={1} customSelectHandler={handleSelect} />
//         </div>

//         {!customCombineHandler && (
//           <div className="flex flex-col sm:flex-row gap-3 justify-center w-full">
//             <button
//               disabled={!canCombine}
//               onClick={handleCombine}
//               className="px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 hover:bg-blue-700 transition-colors w-full sm:w-auto"
//             >
//               Combine
//             </button>

//             <button
//               onClick={handleClearAll}
//               disabled={!selected.some(Boolean)}
//               className={`px-4 sm:px-6 py-2 rounded-lg w-full sm:w-auto ${
//                 selected.some(Boolean)
//                   ? 'bg-red-600 hover:bg-red-700 text-white'
//                   : 'bg-gray-600 text-gray-400 cursor-not-allowed'
//               }`}
//             >
//               Clear All
//             </button>
//           </div>
//         )}
//       </div>
//     </main>
//   );
// };

// export default Arena;
